import { useState, useEffect } from "react";
import { Box, InputAdornment, TextField } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import SearchIcon from "@mui/icons-material/Search";
import ClassroomList from "@nualang/nualang-ui-components/dist/Lists/Classrooms";
import ClassroomsNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Classrooms";
import InfiniteScroll from "@nualang/nualang-ui-components/dist/Misc/InfiniteScroll";
import LanguageSelector from "@nualang/nualang-ui-components/dist/Forms/LanguageSelector/LanguageSelector";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import HideOnScroll from "@nualang/nualang-ui-components/dist/Misc/HideOnScroll";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  box: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  actions: {
    marginBottom: theme.spacing(1),
  },
  autocomplete: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
}));

function SearchFilters({
  t,
  classes,
  languageOptions,
  forLanguageOptions,
  handleFilterChange,
  subscription,
  isUpgradeSubscriptionOpen,
  learnLang,
  search,
  setSearch,
  forLang,
  handleCloseUpgradeSubcriptionModal,
  handleSubscriptionPage,
}) {
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);
  useEffect(() => {
    if (setSearch && searchText !== search) {
      setSearch(searchText);
      if (window.scrollY > 100) {
        window.scrollTo(0, 0);
      }
    }
  }, [debouncedSearch]);
  return (
    <HideOnScroll threshold={300}>
      <Box
        className={classes.box}
        sx={[{
          display: 'flex',
          mb: 2
        }, (theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          marginTop: theme.spacing(0.5),
        })]}>
        <Box
          className={classes.actions}
          sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", width: "100%" }}
        >
          <TextField
            id='search'
            name='search'
            data-cy='search'
            label={t("search")}
            placeholder={t("search_classrooms_placeholder") || "Search classrooms"}
            value={searchText}
            onChange={(ev) => setSearchText(ev.target.value)}
            size="small"
            variant="outlined"
            sx={{ flex: "1 1 200px", maxWidth: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <LanguageSelector
            id={"learnLang"}
            name={"learnLang"}
            label={t("learn")}
            size="small"
            variant={"outlined"}
            sx={{ minWidth: 150 }}
            t={t}
            languages={languageOptions}
            value={learnLang}
            onChange={handleFilterChange}
          />
          <LanguageSelector
            id={"forLang"}
            name={"forLang"}
            label={t("speakers_of")}
            size="small"
            variant={"outlined"}
            sx={{ minWidth: 150 }}
            t={t}
            languages={forLanguageOptions}
            value={forLang}
            onChange={handleFilterChange}
          />
        </Box>
        <UpgradeSubscription
          key={"UpgradeSubscription"}
          t={t}
          open={isUpgradeSubscriptionOpen}
          handleClose={handleCloseUpgradeSubcriptionModal}
          onConfirm={handleSubscriptionPage}
          subscription={subscription}
        />
      </Box>
    </HideOnScroll>
  );
}
export default function SearchClassrooms({
  t = (text) => text,
  classrooms = [],
  isLoading,
  loadMoreClassrooms,
  hasMoreClassrooms,
  placeholderImageUrl,
  memberPlaceholderImageUrl,
  learnLang,
  forLang,
  handleFilterChange,
  languages,
  forLanguages,
  handleJoinClassroom,
  handleLeaveClassroom,
  handleViewClassroom,
  handleCreateClassroom,
  scrollableTarget,
  membersProps,
  getMemberDetails,
  getClassroomMember,
  getClassroomMembers,
  username,
  subscription,
  handleViewUserProfile,
  mainSiteUrl,
  search,
  setSearch,
}) {
  const { classes } = useStyles();
  const languageOptions = {
    all: {},
    ...languages,
  };
  const forLanguageOptions = {
    all: {},
    ...forLanguages,
  };

  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  const handleOpenUpgradeSubscriptionModal = () => {
    setIsUpgradeSubscriptionOpen(true);
  };
  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };
  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  return (
    <div>
      <SearchFilters
        t={t}
        classes={classes}
        languageOptions={languageOptions}
        forLanguageOptions={forLanguageOptions}
        handleFilterChange={handleFilterChange}
        learnLang={learnLang}
        search={search}
        setSearch={setSearch}
        handleCreateClassroom={handleCreateClassroom}
        subscription={subscription}
        handleOpenUpgradeSubscriptionModal={handleOpenUpgradeSubscriptionModal}
        handleCloseUpgradeSubcriptionModal={handleCloseUpgradeSubcriptionModal}
        isUpgradeSubscriptionOpen={isUpgradeSubscriptionOpen}
        handleSubscriptionPage={handleSubscriptionPage}
        forLang={forLang}
      />
      {isLoading || classrooms.length > 0 ? (
        <InfiniteScroll
          dataLength={classrooms.length}
          loadMore={loadMoreClassrooms}
          hasMore={hasMoreClassrooms}
          scrollableTarget={scrollableTarget}
        >
          <div className={classes.root}>
            <ClassroomList
              t={t}
              memberPlaceholderImageUrl={memberPlaceholderImageUrl}
              classrooms={
                Array.isArray(classrooms) && classrooms.length
                  ? classrooms
                  : null
              }
              handleJoinClassroom={handleJoinClassroom}
              handleLeaveClassroom={handleLeaveClassroom}
              handleViewClassroom={handleViewClassroom}
              membersProps={membersProps}
              getMemberDetails={getMemberDetails}
              getClassroomMember={getClassroomMember}
              getClassroomMembers={getClassroomMembers}
              username={username}
              handleViewUserProfile={handleViewUserProfile}
            />
          </div>
        </InfiniteScroll>
      ) : (
        <div className={classes.root}>
          <ClassroomsNotFound
            t={t}
            placeholderImageUrl={placeholderImageUrl}
            handleCreateClassroom={handleCreateClassroom}
            title={t("classrooms_not_found_title")}
            description={t("classrooms_not_found_desc")}
            subscription={subscription}
            mainSiteUrl={mainSiteUrl}
          />
        </div>
      )}
    </div>
  );
}
